<script lang="ts">
  import { t } from '$lib/i18n/index.svelte';
  import { recordingSupported, VoiceRecorder, type VoiceRecording } from '$lib/media/recorder';
  import { containsLink } from '$lib/protocol/link-guard';

  let {
    disabled = false,
    onsendtext,
    onsendimage,
    onsendvoice,
  }: {
    disabled?: boolean;
    onsendtext: (text: string) => void;
    onsendimage: (file: File) => void;
    onsendvoice: (recording: VoiceRecording) => void;
  } = $props();

  let text = $state('');
  let linkWarning = $state(false);
  let micDenied = $state(false);
  let recorder = $state<VoiceRecorder | null>(null);
  let elapsedS = $state(0);
  let fileInput = $state<HTMLInputElement | null>(null);
  let timer: ReturnType<typeof setInterval> | null = null;

  function send(): void {
    const trimmed = text.trim();
    if (trimmed.length === 0) {
      return;
    }
    // Checked before sending so the user is told immediately, not after a
    // failed request — the server enforces the same ban regardless.
    if (containsLink(trimmed)) {
      linkWarning = true;
      return;
    }
    linkWarning = false;
    onsendtext(trimmed);
    text = '';
  }

  function onFilePicked(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (file !== undefined) {
      onsendimage(file);
    }
    input.value = '';
  }

  async function startRecording(): Promise<void> {
    const fresh = new VoiceRecorder();
    try {
      await fresh.start();
    } catch {
      micDenied = true;
      return;
    }
    micDenied = false;
    recorder = fresh;
    elapsedS = 0;
    timer = setInterval(() => {
      elapsedS = Math.floor(fresh.elapsedMs / 1000);
    }, 250);
  }

  function stopTimer(): void {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  }

  async function stopRecording(): Promise<void> {
    const current = recorder;
    if (current === null) {
      return;
    }
    stopTimer();
    recorder = null;
    if (current.elapsedMs < 200) {
      current.cancel();
      return;
    }
    const result = await current.stop();
    if (result !== undefined) {
      onsendvoice(result);
    }
  }

  function cancelRecording(): void {
    stopTimer();
    recorder?.cancel();
    recorder = null;
  }
</script>

<div class="compose">
  {#if linkWarning}
    <div class="warning">{t('conv.linkBlocked')}</div>
  {/if}
  {#if micDenied}
    <div class="warning">{t('conv.micDenied')}</div>
  {/if}
  {#if recorder !== null}
    <div class="recording">
      <span class="dot"></span>
      <span>{t('conv.recording', { s: elapsedS })}</span>
      <button class="ghost" onclick={cancelRecording}>{t('conv.recordingCancel')}</button>
      <button class="primary" onclick={() => void stopRecording()}>{t('conv.recordingStop')}</button>
    </div>
  {:else}
    <div class="row">
      <input
        type="file"
        accept="image/jpeg,image/png"
        hidden
        bind:this={fileInput}
        onchange={onFilePicked}
      />
      <button
        class="icon"
        title={t('conv.attachPhoto')}
        aria-label={t('conv.attachPhoto')}
        {disabled}
        onclick={() => fileInput?.click()}>📎</button
      >
      <textarea
        rows="1"
        placeholder={t('conv.composerHint')}
        {disabled}
        bind:value={text}
        oninput={() => (linkWarning = false)}
        onkeydown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            send();
          }
        }}
      ></textarea>
      {#if text.trim().length > 0}
        <button class="primary" {disabled} onclick={send}>{t('conv.send')}</button>
      {:else if recordingSupported()}
        <button
          class="icon"
          title={t('conv.recordVoice')}
          aria-label={t('conv.recordVoice')}
          {disabled}
          onclick={() => void startRecording()}>🎤</button
        >
      {/if}
    </div>
  {/if}
</div>

<style>
  .compose {
    border-top: 1px solid var(--line);
    background: var(--surface);
    padding: 8px 12px calc(8px + env(safe-area-inset-bottom, 0px));
  }
  .row {
    display: flex;
    gap: 8px;
    align-items: flex-end;
  }
  textarea {
    flex: 1;
    resize: none;
    border: 1px solid var(--line);
    border-radius: 18px;
    padding: 10px 14px;
    font: inherit;
    background: var(--bg);
    color: var(--ink);
    max-height: 120px;
  }
  textarea:focus {
    outline: 2px solid var(--brand);
    border-color: transparent;
  }
  .icon {
    border: 0;
    background: transparent;
    font-size: 22px;
    cursor: pointer;
    padding: 8px;
  }
  .primary {
    border: 0;
    border-radius: 999px;
    background: var(--brand);
    color: var(--brand-ink);
    font-weight: 600;
    padding: 10px 18px;
    cursor: pointer;
  }
  .ghost {
    border: 0;
    background: transparent;
    color: var(--ink-soft);
    cursor: pointer;
    padding: 10px;
  }
  .warning {
    color: var(--danger);
    font-size: 13px;
    padding: 4px 8px;
  }
  .recording {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 4px;
  }
  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--danger);
    animation: blink 1s infinite;
  }
  @keyframes blink {
    50% {
      opacity: 0.2;
    }
  }
</style>
